import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Icon from '../ui/Icon';
import { AudioFX, CourierBadge } from '../ui/primitives';
import type { CourierKey } from '../ui/couriers';
import { API_URL } from '../config/api';

/* ============================================================================
   Ekran drukowania stacji. Każdy wydruk ze stacji (etykieta przesyłki po zamknięciu paczki,
   etykiety produktów, lista pakowania, przedruki, etykieta logo, raport) otwiera ten sam ekran:
   od razu po kliknięciu — operator widzi, że przycisk zadziałał — potem status zadania z kolejki
   print_jobs (w kolejce → drukowanie → gotowe) z licznikiem etykiet, a na końcu pytanie, czy
   wydruk jest dobry, z „Drukuj ponownie". Przy włączonej kontroli adresu potwierdzenie etykiety
   przesyłki na adres pokazuje dodatkowo surowy adres z zamówienia do porównania z wydrukiem.
   ========================================================================== */

export type PrintUnit = 'label' | 'page';

/** Surowe dane z zamówienia Shopify — tak, jak wpisał je klient, zanim przetworzy je przewoźnik. */
export type AddressCheckData = {
  line1: string | null;
  line2: string | null;
  postalCode: string | null;
  city: string | null;
  note: string | null;
  orderName: string;
  packageNumber?: number;
  courier?: CourierKey | null;
};

export type PrintRunResult = {
  /** Zadania druku do śledzenia. Puste = nic nie trafiło do kolejki (patrz niżej). */
  jobIds: string[];
  /** Świadomie nic nie drukowano (np. pusty raport) — pokaż wyjaśnienie zamiast pytania. */
  info?: string;
  /** Druk padł, zanim powstało zadanie (np. drukarka bez agenta) — błąd z „Drukuj ponownie". */
  error?: string;
  /** Nie ma czego pokazywać (np. auto-druk wyłączony) — zamknij ekran bez pytania. */
  silent?: boolean;
  /** Dodatkowa linia pod tytułem, np. numer przesyłki. */
  detail?: string;
  /** Numer przesyłki na etykiecie kontroli adresu. */
  trackingNumber?: string | null;
  /** Jak powtórzyć, gdy nie ma zadania do ponownego druku (np. ponowny wydruk etykiety paczki). */
  redo?: () => Promise<PrintRunResult>;
};

/** Odpowiedź endpointu druku (`{ jobId }`) → zadanie do śledzenia. */
export function jobsFrom(data: { jobId?: string | null } | null | undefined): PrintRunResult {
  return { jobIds: data?.jobId ? [String(data.jobId)] : [] };
}

/** Jak zamknięto ekran: `ok` = operator potwierdził, `hidden` = ukrył w trakcie, `failed` = po błędzie. */
export type PrintOutcome = 'ok' | 'hidden' | 'failed';

export type PrintRequest = {
  title: string;
  subtitle?: string;
  /** Co robi pierwszy krok, zanim wróci odpowiedź (domyślnie „Przygotowanie wydruku…"). */
  preparing?: string;
  /** Nazwa pierwszego kroku na osi (domyślnie „Przygotowanie"). */
  firstStep?: string;
  courier?: CourierKey | null;
  /** `label` liczy etykiety, `page` strony (dokumenty A4). */
  unit?: PrintUnit;
  /** Wysyła wydruk. Woła go od razu startPrint (kliknięcie), a ponownie tylko „Spróbuj ponownie". */
  run: () => Promise<PrintRunResult>;
  addressCheck?: AddressCheckData | null;
  onClose?: (outcome: PrintOutcome) => void;
};

type JobStatus = {
  id: string;
  documentType?: string | null;
  status: string;
  error?: string | null;
  copies?: number | null;
  labelCount?: number | null;
  printerName?: string | null;
};

type Settled = { ok: true; result: PrintRunResult } | { ok: false; message: string };

type Phase =
  | { kind: 'preparing' }
  | { kind: 'printing' }
  | { kind: 'confirm' }
  | { kind: 'failed'; message: string; stage: 'request' | 'print' }
  | { kind: 'info'; message: string };

const TERMINAL = new Set(['done', 'error', 'cancelled']);
/** Po tylu sekundach w kolejce podpowiadamy, że agent druku może nie działać. */
const QUEUE_HINT_AFTER_S = 20;
/** Po tylu sekundach „drukowania" podpowiadamy sprawdzenie drukarki. */
const PRINTING_HINT_AFTER_S = 45;
/**
 * „Ukryj" w trakcie samego żądania (np. zakup etykiety u przewoźnika) pojawia się dopiero po tym
 * czasie: ukryty ekran nie pokaże, że żądanie padło, a zwykle trwa ono kilka sekund. Zostaje jako
 * wyjście awaryjne, gdyby serwer nie odpowiadał.
 */
const HIDE_WHILE_PREPARING_AFTER_S = 20;

function errorText(e: any): string {
  const data = e?.response?.data;
  if (typeof data?.error === 'string' && data.error) return data.error;
  if (typeof data?.message === 'string' && data.message) return data.message;
  if (e?.code === 'ECONNABORTED') return 'Serwer nie odpowiedział na czas — spróbuj ponownie.';
  if (e?.message === 'Network Error') return 'Brak połączenia z serwerem.';
  return e?.message || 'Nieznany błąd druku';
}

async function settle(fn: () => Promise<PrintRunResult>): Promise<Settled> {
  try {
    return { ok: true, result: await fn() };
  } catch (e) {
    return { ok: false, message: errorText(e) };
  }
}

// Odmiana (1 / 2–4 / 5+, z wyjątkiem 12–14): [biernik lp., mianownik lm., dopełniacz lm.].
function plural(n: number, forms: [string, string, string]): string {
  const abs = Math.abs(n);
  if (abs === 1) return forms[0];
  const t = abs % 10;
  const h = abs % 100;
  return t >= 2 && t <= 4 && (h < 12 || h > 14) ? forms[1] : forms[2];
}
const UNIT_FORMS: Record<PrintUnit, [string, string, string]> = {
  label: ['etykietę', 'etykiety', 'etykiet'],
  page: ['stronę', 'strony', 'stron'],
};

/** Status zadań; starsze API bez GET /jobs?ids= (404) — szczegóły zadania po kolei. */
async function fetchJobStatuses(ids: string[]): Promise<JobStatus[]> {
  try {
    const r = await axios.get(`${API_URL}/api/printing/jobs`, { params: { ids: ids.join(',') }, timeout: 8000 });
    return Array.isArray(r.data?.jobs) ? r.data.jobs : [];
  } catch (e: any) {
    if (e?.response?.status !== 404) throw e;
    const details = await Promise.all(
      ids.map((id) => axios.get(`${API_URL}/api/printing/jobs/${id}`, { timeout: 8000 }).then((r) => r.data)),
    );
    return details.map((d: any) => ({
      id: d.id,
      documentType: d.documentType,
      status: d.status,
      error: d.error,
      copies: d.copies,
      printerName: d.printerName,
    }));
  }
}

/* ---------------------------------------------------------------------------- */

type PrintCtx = { startPrint: (request: PrintRequest) => void; printing: boolean };
const Ctx = createContext<PrintCtx | null>(null);

export function usePrint(): PrintCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('usePrint must be inside PrintProvider');
  return ctx;
}

type Active = { id: number; request: PrintRequest; first: Promise<Settled> };

export function PrintProvider({ children }: { children: React.ReactNode }) {
  const [active, setActive] = useState<Active | null>(null);
  const seq = useRef(0);

  const startPrint = useCallback((request: PrintRequest) => {
    // Żądanie leci TU, w obsłudze kliknięcia, a nie w efekcie: StrictMode (dev) odpala efekty
    // montowania dwa razy, a drugi POST to drugi wydruk.
    const first = settle(request.run);
    seq.current += 1;
    setActive({ id: seq.current, request, first });
  }, []);

  const value = useMemo(() => ({ startPrint, printing: active !== null }), [startPrint, active]);

  return (
    <Ctx.Provider value={value}>
      {children}
      {active && (
        <PrintOverlay
          key={active.id}
          request={active.request}
          first={active.first}
          onDone={(outcome) => {
            setActive((cur) => (cur?.id === active.id ? null : cur));
            active.request.onClose?.(outcome);
          }}
        />
      )}
    </Ctx.Provider>
  );
}

/* ---------------------------------------------------------------------------- */

function PrintOverlay({ request, first, onDone }: {
  request: PrintRequest;
  first: Promise<Settled>;
  onDone: (outcome: PrintOutcome) => void;
}) {
  const [phase, setPhase] = useState<Phase>({ kind: 'preparing' });
  const [jobIds, setJobIds] = useState<string[]>([]);
  const [jobs, setJobs] = useState<JobStatus[]>([]);
  const [detail, setDetail] = useState<string | undefined>(undefined);
  const [tracking, setTracking] = useState<string | null>(null);
  const [reprints, setReprints] = useState(0);
  const [pollTrouble, setPollTrouble] = useState(false);
  const [phaseSince, setPhaseSince] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  const redoRef = useRef<(() => Promise<PrintRunResult>) | null>(null);
  const closedRef = useRef(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const unit = request.unit || 'label';

  const finish = useCallback((outcome: PrintOutcome) => {
    if (closedRef.current) return;
    closedRef.current = true;
    onDoneRef.current(outcome);
  }, []);

  const enter = useCallback((next: Phase) => {
    setPhase(next);
    setPhaseSince(Date.now());
  }, []);

  const apply = useCallback((s: Settled) => {
    if (closedRef.current) return;
    if (!s.ok) {
      AudioFX.buzz();
      enter({ kind: 'failed', message: s.message, stage: 'request' });
      return;
    }
    const r = s.result;
    if (r.detail !== undefined) setDetail(r.detail);
    if (r.trackingNumber !== undefined) setTracking(r.trackingNumber ?? null);
    if (r.redo) redoRef.current = r.redo;
    if (r.silent) { finish('ok'); return; }
    if (r.jobIds.length > 0) {
      setJobIds(r.jobIds);
      setJobs([]);
      enter({ kind: 'printing' });
      return;
    }
    if (r.error) {
      AudioFX.buzz();
      enter({ kind: 'failed', message: r.error, stage: 'print' });
      return;
    }
    enter({ kind: 'info', message: r.info || 'Nic nie zostało wysłane do druku.' });
  }, [enter, finish]);
  const applyRef = useRef(apply);
  applyRef.current = apply;

  // Pierwsza odpowiedź (żądanie wysłane w kliknięciu, patrz PrintProvider).
  useEffect(() => {
    let live = true;
    first.then((s) => { if (live) applyRef.current(s); });
    return () => { live = false; };
  }, [first]);

  // Status zadań z kolejki: co 1 s, po 20 s co 2,5 s, aż wszystkie się skończą.
  useEffect(() => {
    if (phase.kind !== 'printing' || jobIds.length === 0) return;
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let failures = 0;
    const started = Date.now();
    const tick = async () => {
      try {
        const found = await fetchJobStatuses(jobIds);
        if (!live) return;
        failures = 0;
        setPollTrouble(false);
        const byId = new Map(found.map((j) => [j.id, j]));
        // Zadanie, którego kolejka nie zna, nie skończy się nigdy — pokazujemy je jako błąd.
        const list = jobIds.map((id) => byId.get(id) || { id, status: 'error', error: 'Nie znaleziono zadania druku w kolejce' });
        setJobs(list);
        if (list.every((j) => TERMINAL.has(j.status))) {
          const failed = list.filter((j) => j.status !== 'done');
          if (failed.length > 0) {
            AudioFX.buzz();
            const reasons = Array.from(new Set(failed.map((j) => j.error || (j.status === 'cancelled' ? 'Zadanie anulowano' : 'Drukarka zgłosiła błąd'))));
            enter({ kind: 'failed', message: reasons.join(' · '), stage: 'print' });
          } else {
            AudioFX.success();
            enter({ kind: 'confirm' });
          }
          return;
        }
      } catch {
        if (!live) return;
        failures += 1;
        if (failures >= 3) setPollTrouble(true);
      }
      timer = setTimeout(tick, Date.now() - started < 20000 ? 1000 : 2500);
    };
    tick();
    return () => { live = false; if (timer) clearTimeout(timer); };
  }, [phase.kind, jobIds, enter]);

  // Zegar do licznika sekund i podpowiedzi „czeka za długo".
  useEffect(() => {
    if (phase.kind !== 'preparing' && phase.kind !== 'printing') return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [phase.kind]);

  // Fokus na oknie (nie na przycisku): skaner kodów kończy odczyt Enterem, który inaczej
  // „kliknąłby" podświetlony przycisk bez udziału operatora.
  useEffect(() => { boxRef.current?.focus(); }, [phase.kind]);

  async function redo() {
    const failedIds = jobs.filter((j) => j.status === 'error' || j.status === 'cancelled').map((j) => j.id);
    // Z dwóch etykiet powtarzamy tylko tę, która nie wyszła; gdy wyszły wszystkie — całość.
    const toReprint = failedIds.length > 0 && failedIds.length < jobIds.length ? failedIds : jobIds;
    AudioFX.click();
    setReprints((n) => n + 1);
    setJobs([]);
    setPollTrouble(false);
    enter({ kind: 'preparing' });
    if (toReprint.length > 0) {
      apply(await settle(async () => {
        const ids: string[] = [];
        for (const id of toReprint) {
          const r = await axios.post(`${API_URL}/api/printing/jobs/${id}/reprint`);
          if (r.data?.jobId) ids.push(r.data.jobId);
        }
        return { jobIds: ids };
      }));
      return;
    }
    apply(await settle(redoRef.current || request.run));
  }

  const elapsed = Math.max(0, Math.round((now - phaseSince) / 1000));
  const queued = jobs.some((j) => j.status === 'queued') && !jobs.some((j) => j.status === 'picked' || j.status === 'printing');
  const counts = useMemo(() => {
    if (jobs.length === 0) return null;
    let total = 0;
    let printed = 0;
    for (const j of jobs) {
      const n = j.labelCount ?? j.copies ?? 1;
      total += n;
      if (j.status === 'done') printed += n;
    }
    return { total, printed, left: total - printed };
  }, [jobs]);
  const printerNames = Array.from(new Set(jobs.map((j) => j.printerName).filter(Boolean))).join(', ');

  // Oś kroków: 0 przygotowanie, 1 kolejka, 2 drukowanie, 3 gotowe. Błąd druku bez zadania (np.
  // drukarka bez agenta) to krok kolejki, błąd zgłoszony przez agenta — krok drukowania.
  let activeStep = 0;
  let failedStep: number | null = null;
  if (phase.kind === 'printing') activeStep = jobs.length === 0 || queued ? 1 : 2;
  if (phase.kind === 'confirm') activeStep = 4;
  if (phase.kind === 'failed') failedStep = phase.stage === 'request' ? 0 : jobs.length > 0 ? 2 : 1;

  const busy = phase.kind === 'preparing' || phase.kind === 'printing';
  const canHide = phase.kind === 'printing' || (phase.kind === 'preparing' && elapsed >= HIDE_WHILE_PREPARING_AFTER_S);
  let statusLine: string;
  if (phase.kind === 'preparing') {
    statusLine = reprints > 0 ? 'Ponowne wysyłanie do drukarki…' : request.preparing || 'Przygotowanie wydruku…';
  } else if (phase.kind === 'printing') {
    statusLine = jobs.length === 0
      ? 'Wysłano do kolejki druku…'
      : queued ? 'Czeka na komputer drukujący…' : 'Drukarka drukuje…';
  } else statusLine = '';

  const eyebrow = busy ? 'Drukowanie' : phase.kind === 'confirm' ? 'Wydrukowano' : phase.kind === 'failed' ? 'Błąd druku' : 'Informacja';
  const glyph = phase.kind === 'confirm' ? 'check' : phase.kind === 'failed' ? 'alert-triangle' : phase.kind === 'info' ? 'info' : 'printer';
  const check = phase.kind === 'confirm' ? request.addressCheck : null;

  return (
    <div className="scrim center pp-scrim">
      <div
        className={`sheet dialog pp pp-${phase.kind}${check ? ' pp-wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pp-title"
        tabIndex={-1}
        ref={boxRef}
      >
        <div className="pp-head">
          <div className={`pp-glyph${busy ? ' busy' : ''}`}><Icon name={glyph} size={28} /></div>
          <div className="pp-head-text">
            <div className="pp-eyebrow">{eyebrow}{reprints > 0 ? ' · ponowny wydruk' : ''}</div>
            <h3 id="pp-title">{request.title}</h3>
            {request.subtitle && <div className="pp-sub">{request.subtitle}</div>}
          </div>
          {request.courier && request.courier !== 'unknown' && <CourierBadge courier={request.courier} lg />}
        </div>

        {phase.kind !== 'info' && (
          <ol className="pp-steps" aria-label="Postęp wydruku">
            {[request.firstStep || 'Przygotowanie', 'W kolejce', 'Drukowanie', 'Gotowe'].map((label, i) => {
              const state = failedStep === i ? 'err'
                : failedStep !== null ? (i < failedStep ? 'done' : 'todo')
                : i < activeStep ? 'done' : i === activeStep ? 'now' : 'todo';
              return (
                <li key={label} className={`pp-step ${state}`}>
                  <span className="pp-dot">
                    {state === 'done' ? <Icon name="check" size={14} /> : state === 'err' ? <Icon name="x" size={14} /> : i + 1}
                  </span>
                  <span className="pp-step-label">{label}</span>
                </li>
              );
            })}
          </ol>
        )}

        {/* Przy kontroli adresu numer przesyłki jest w stopce etykiety — bez dublowania. */}
        {detail && !check && <div className="pp-detail mono">{detail}</div>}

        {busy && (
          <>
            <div className="pp-status" aria-live="polite">{statusLine}</div>
            <div className="pp-meter">
              <div className="pp-meter-top">
                <span>{unit === 'page' ? 'Strony' : 'Etykiety'}</span>
                <b className="tnum">{counts ? `${counts.printed} / ${counts.total}` : '—'}</b>
              </div>
              <div className={`kprog pp-bar${phase.kind === 'printing' && counts && counts.printed === 0 ? ' indet' : ''}`}>
                <i style={{ width: counts && counts.total ? `${Math.round((counts.printed / counts.total) * 100)}%` : '0%' }} />
              </div>
              <div className="pp-meter-meta">
                {counts && <span>pozostało <b className="tnum">{counts.left}</b></span>}
                {printerNames && <span><Icon name="printer" size={13} /> {printerNames}</span>}
                <span className="tnum"><Icon name="timer" size={13} /> {elapsed} s</span>
              </div>
            </div>
            {phase.kind === 'printing' && queued && elapsed >= QUEUE_HINT_AFTER_S && (
              <div className="pp-hint">
                <Icon name="alert-triangle" size={16} />
                <span>Zadanie czeka w kolejce od {elapsed} s. Sprawdź, czy komputer drukujący (agent druku) jest włączony i ma internet — wydruk ruszy sam, gdy agent go pobierze.</span>
              </div>
            )}
            {phase.kind === 'printing' && !queued && jobs.length > 0 && elapsed >= PRINTING_HINT_AFTER_S && (
              <div className="pp-hint">
                <Icon name="alert-triangle" size={16} />
                <span>Drukarka nie potwierdziła wydruku od {elapsed} s. Sprawdź papier, pokrywę i kabel sieciowy drukarki.</span>
              </div>
            )}
            {pollTrouble && (
              <div className="pp-hint">
                <Icon name="wifi-off" size={16} />
                <span>Brak odpowiedzi serwera — sprawdzam status ponownie…</span>
              </div>
            )}
            {canHide && (
              <button className="big-btn bb-ghost pp-full" onClick={() => finish('hidden')}>
                <Icon name="eye-off" size={20} /> Ukryj — druk dokończy się w tle
              </button>
            )}
          </>
        )}

        {phase.kind === 'confirm' && (() => {
          const summary = (
            <div className="pp-done">
              {counts
                ? <>Wydrukowano <b className="tnum">{counts.total}</b> {plural(counts.total, UNIT_FORMS[unit])}.</>
                : <>Wydruk zakończony.</>}
              {printerNames && <span className="pp-done-meta"> Drukarka: {printerNames}</span>}
            </div>
          );
          const question = (
            <div className="pp-question">
              {check ? 'Czy etykieta wydrukowała się poprawnie i adres się zgadza?' : 'Czy wszystko wydrukowało się poprawnie?'}
            </div>
          );
          const actions = (
            <div className="pp-actions">
              <button className="big-btn bb-ghost" onClick={redo}>
                <Icon name="rotate-ccw" size={20} /> Drukuj ponownie
              </button>
              <button className="big-btn bb-success" onClick={() => { AudioFX.click(); finish('ok'); }}>
                <Icon name="check" size={20} /> {check ? 'Wszystko się zgadza' : 'Tak, wszystko OK'}
              </button>
            </div>
          );
          if (!check) return <>{summary}{question}{actions}</>;
          // Etykieta obok pytania, żeby na ekranie stacji wszystko było widać bez przewijania;
          // na wąskim ekranie kolumny same układają się jedna pod drugą.
          return (
            <div className="pp-check">
              <div className="pp-check-main"><AddressCheckLabel data={check} tracking={tracking} /></div>
              <div className="pp-check-side">{summary}{question}{actions}</div>
            </div>
          );
        })()}

        {phase.kind === 'failed' && (
          <>
            <div className="pp-error">
              <Icon name="alert-circle" size={18} />
              <span>{phase.message}</span>
            </div>
            <div className="pp-question">
              {phase.stage === 'request'
                ? 'Nic nie trafiło do drukarki. Spróbować jeszcze raz?'
                : 'Wydruk się nie udał. Sprawdź drukarkę i spróbuj ponownie.'}
            </div>
            <div className="pp-actions">
              <button className="big-btn bb-ghost" onClick={() => finish('failed')}>Zamknij</button>
              <button className="big-btn bb-primary" onClick={redo}>
                <Icon name="rotate-ccw" size={20} /> {phase.stage === 'request' ? 'Spróbuj ponownie' : 'Drukuj ponownie'}
              </button>
            </div>
          </>
        )}

        {phase.kind === 'info' && (
          <>
            <div className="pp-status">{phase.message}</div>
            <button className="big-btn bb-primary pp-full" onClick={() => finish('ok')}>OK</button>
          </>
        )}
      </div>
    </div>
  );
}

/* ---- kontrola adresu: surowe dane z zamówienia w formie uproszczonej etykiety ---- */

function LabelField({ label, value, empty, size }: {
  label: string;
  value: string | null;
  empty: string;
  size?: 'big' | 'mid';
}) {
  const has = !!value && value.trim().length > 0;
  return (
    <div className={`pp-field${size ? ` ${size}` : ''}${has ? '' : ' pp-blank'}`}>
      <div className="pp-field-k">{label}</div>
      <div className="pp-field-v">{has ? value : empty}</div>
    </div>
  );
}

// Dekoracja w stylu etykiety — paski z cyfr numeru przesyłki, NIE kod do skanowania
// (numer obok jest tekstem; prawdziwy kod kreskowy jest na wydrukowanej etykiecie).
function DecorBars({ seed }: { seed: string }) {
  const widths: number[] = [];
  for (const ch of seed) {
    const c = ch.charCodeAt(0);
    widths.push(1 + (c % 3), 1 + ((c >> 1) % 2), 1 + ((c >> 2) % 3), 1 + ((c >> 3) % 2));
  }
  let x = 0;
  const bars: React.ReactNode[] = [];
  widths.forEach((w, i) => {
    if (i % 2 === 0) bars.push(<rect key={i} x={x} y={0} width={w} height={10} />);
    x += w;
  });
  return (
    <svg className="pp-bars" viewBox={`0 0 ${Math.max(1, x)} 10`} preserveAspectRatio="none" aria-hidden="true">
      {bars}
    </svg>
  );
}

export function AddressCheckLabel({ data, tracking }: { data: AddressCheckData; tracking: string | null }) {
  return (
    <>
      <section className={`pp-label${data.courier ? ` pp-label-${data.courier}` : ''}`} aria-label="Kontrola adresu">
        <div className="pp-label-head">
          <span className="pp-label-kicker"><Icon name="map-pin" size={14} /> Kontrola adresu</span>
          <span className="pp-label-order">
            {data.orderName}{data.packageNumber ? ` · Paczka ${data.packageNumber}` : ''}
          </span>
        </div>
        <div className="pp-label-body">
          <div className="pp-label-caption">Adres z zamówienia — dokładnie tak, jak wpisał go klient</div>
          <LabelField label="Adres — linia 1" value={data.line1} empty="pusta" size="big" />
          <LabelField label="Adres — linia 2" value={data.line2} empty="pusta" size="big" />
          <div className="pp-label-row">
            <LabelField label="Kod pocztowy" value={data.postalCode} empty="pusty" size="mid" />
            <LabelField label="Miejscowość" value={data.city} empty="pusta" size="mid" />
          </div>
          <div className="pp-label-rule" />
          <LabelField label="Uwagi do zamówienia" value={data.note} empty="brak uwag" />
        </div>
        {tracking && (
          <div className="pp-label-foot">
            <DecorBars seed={tracking} />
            <span className="pp-label-track">{tracking}</span>
          </div>
        )}
      </section>
      <p className="pp-label-hint">
        Porównaj z wydrukowaną etykietą — zwłaszcza numer domu i mieszkania. Coś się nie zgadza? Zgłoś
        zamówienie administratorowi, zanim paczka wyjdzie.
      </p>
    </>
  );
}
